import React, { useState } from 'react';
import UserForm from '../../components/UserForm';
import routes from '../../routes';
import PhoneConfirmationCodeDialog from '../../components/PhoneConfirmationCodeDialog';
import Bluebird from 'bluebird';
import snackbarHelper from '../../helpers/snackbarHelper';
import * as usersActions from '../../actions/users';
import * as rescuesActions from '../../actions/rescues';
import * as rescuersActions from '../../actions/rescuers';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as usersApi from '../../api/users';
import { formatPhoneNumber } from '../../helpers/formatters';
import { getUserSitesByRoles, setDefaultSiteAndSites, currentlyLoggedInOrImpersonatingUserHasRoleInCurrentlySelectedSite } from '../../services/auth';
import { Roles } from '../../models/roles';
import VerificationPauseRescueConfirmationDialog from '../../components/VerificationPauseRescueConfirmationDialog';
import VerificationResumeRescueConfirmationDialog from '../../components/VerificationResumeRescueConfirmationDialog';
import useImpersonatedUser from '../../hooks/useImpersonatedUser';
import { getUserImage, isRescuerPausedAtAnySite } from '../../helpers/user';

const ProfileView = props => {
  const { user, rescuerLocationTypes, upcomingRescues, isMobileProfileView } = props;
  const [showPhoneNumberVerificationDialog, setShowPhoneNumberVerificationDialog] = useState(false);
  const [showVerificationPauseRescueDialog, setShowVerificationPauseRescueDialog] = useState(false);
  const [showVerificationResumeRescueDialog, setShowVerificationResumeRescueDialog] = useState(false);
  const [verificationDialogLoading, setVerificationDialogLoading] = useState(false);
  const [confirmationCodeError, setConfirmationCodeError] = useState(false);
  const [switchSites, setSwitchSites] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'));
  const { role_assignments: userRoleAssignments } = user;
  const isRescuer = currentlyLoggedInOrImpersonatingUserHasRoleInCurrentlySelectedSite(Roles.Rescuer);
  const sitesWithRescuerRoles = getUserSitesByRoles(userRoleAssignments, Roles.Rescuer).length;
  const isRescuerInMultipleSites = sitesWithRescuerRoles > 1;
  const updateUser = (userId, data) => dispatch(usersActions.updateUser(userId, data));
  const sitesEntites = useSelector((state) => state.entities.sites);
  const sitesList = Object.values(sitesEntites.byId);
  const impersonatedUser = useImpersonatedUser();
  const idTokenPayload = useSelector((state) => state.app.idTokenPayload);
  const picture = getUserImage(user, impersonatedUser ? null : idTokenPayload);

  const handleCodeNotReceived = () => {
    setVerificationDialogLoading(true);

    return Bluebird
      .try(() => usersApi.sendSmsVerifyRequest(user.id))
      .then(() => setVerificationDialogLoading(false))
      .then(() => setConfirmationCodeError(false));
  };

  const handleVerify = code => Bluebird
    .try(() => updateUser(user.id, { phone_verification_code: code }))
    .then(user => {
      if (user.phone_verified === null) {
        return Promise.reject();
      }

      handleClose();
      snackbarHelper.success('You have successfully verified your phone number!', 10000);
    })
    .catch(() => {
      setVerificationDialogLoading(false);
      setConfirmationCodeError(true);
    });

  const showPhoneVerificationDialog = () => setShowPhoneNumberVerificationDialog(true);

  const handleClose = () => {
    setShowPhoneNumberVerificationDialog(false);
    setVerificationDialogLoading(false);
    setConfirmationCodeError(false);
  };

  const handleSubmit = async (userId, values, isEmailChanged) => {
    const updateUserPayload = {
      ...values,
      send_email_verification: true, // pass a flag, so API can (potentially - email change) send email verification
    };

    if (switchSites) {
      if (switchSites.new_sites) {
        updateUserPayload.site_id = switchSites.new_sites.map(newSite => newSite.id);
        updateUserPayload.roles = ['rescuer'];
      }

      if (switchSites.sites_to_leave && Array.isArray(switchSites.sites_to_leave)) {
        updateUserPayload.role_delete_main_reason = 'LOCATION';
        updateUserPayload.role_delete_reason = '(system) User updated zip code.';
        updateUserPayload.roles_to_detach = switchSites.sites_to_leave.map(siteToLeave => ({
          role_name: 'rescuer',
          site_id: siteToLeave.id,
        }));
      }
    }

    const response = await updateUser(userId, updateUserPayload);

    setSwitchSites(null);

    // user changed his roles (eg. leave site)
    if (updateUserPayload.hasOwnProperty('roles') || updateUserPayload.hasOwnProperty('roles_to_detach')) {
      await Promise.all([
        dispatch(rescuesActions.invalidateRescuesData()),
        dispatch(rescuersActions.invalidateRescuerEvents(userId)),
        setDefaultSiteAndSites(response.role_assignments),
      ]);
    }

    if (!isEmailChanged) {
      return false;
    }

    history.push(routes.emailChanged);
    return true;
  };

  const handlePause = () => {
    setShowVerificationPauseRescueDialog(true);
  };

  const handleResume = () => {
    setShowVerificationResumeRescueDialog(true);
  };

  const handleRescuePause = () => {
    Bluebird.try(() =>
      updateUser(user.id, {
        paused: true,
      })
    );
  };

  const handleRescueResume = () => {
    Bluebird.try(() =>
      updateUser(user.id, {
        paused: false,
      })
    );
  };

  return (
    <>
      <UserForm
        user={user}
        isProfile
        impersonatedUser={impersonatedUser}
        isMobileView={isMobileView}
        isMobileProfileView={isMobileProfileView}
        profilePicture={picture}
        showProfilePicture
        ShowPauseRescue={isRescuer}
        isRescuingPaused={isRescuerPausedAtAnySite(user)}
        updateUser={updateUser}
        pauseCallback={handlePause}
        resumeCallback={handleResume}
        redirectPath={generatePath(routes.profile)}
        submitCallback={handleSubmit}
        submitButtonText="Update Profile"
        showVerifyPhoneNumberButton
        rescuerLocationTypes={rescuerLocationTypes}
        showCommunicationCheckboxes={false}
        showPhoneVerificationDialog={showPhoneVerificationDialog}
        showVerifyEmailButton
        showRescuerRoleAssignmentsTable={isRescuerInMultipleSites}
        showPasswordFieldsOnEmailChange={false}
        sitesList={sitesList}
        upcomingRescues={upcomingRescues}
        setSwitchSites={setSwitchSites}
        showSiteChange
        showLeaveFoodRescueButton
        showQrtCheckbox={isRescuer}
        qrtPopup
      />

      {showVerificationPauseRescueDialog && (
        <VerificationPauseRescueConfirmationDialog 
          onCloseDialog={() => setShowVerificationPauseRescueDialog(false)}
          onPauseButtonClick={handleRescuePause}
        />
      )}

      {showVerificationResumeRescueDialog && (
        <VerificationResumeRescueConfirmationDialog 
          onCloseDialog={() => setShowVerificationResumeRescueDialog(false)}
          onResumeButtonClick={handleRescueResume}
        />
      )}

      <PhoneConfirmationCodeDialog
        phoneNo={formatPhoneNumber(user.phone)}
        handleCodeNotReceived={handleCodeNotReceived}
        handleClose={handleClose}
        handleVerify={handleVerify}
        open={showPhoneNumberVerificationDialog}
        loading={verificationDialogLoading}
        confirmationCodeError={confirmationCodeError}
      />
    </>
  );
};

export default ProfileView;
